// TaskAssignments — /dashboard/tasks/:taskId/assignments
//
// Manage the matching matrix for a single task. Each assignment binds the
// task to (location × route? × RRULE × target). Multiple
// assignments per task are normal; the tap-time matcher unions them.
//
// Backend: /v1/admin/tasks/:taskId/assignments + /v1/admin/assignments/:id.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Plus, RefreshCw, ServerCrash, Inbox, Trash2, ArrowLeft, MapPin,
  Route as RouteIcon, Users as UsersIcon, User as UserIcon, Clock,
  Info,
} from "lucide-react";
import PageHeader from "../components/PageHeader.js";
import EmptyState from "../components/EmptyState.js";
import Skeleton from "../components/Skeleton.js";
import Drawer from "../components/Drawer.js";
import { Field } from "../components/Field.js";
import RRuleEditor from "../components/RRuleEditor.js";
import { humanizeRRule, humanizeWindow } from "../lib/humanRrule.js";
import {
  listAssignmentsForTask, createAssignment, patchAssignment, archiveAssignment,
  friendlyAssignmentsError, type AssignmentRow, type AssignmentBody, type TargetKind,
} from "../lib/assignmentsApi.js";
import { getTask, type TaskRow } from "../lib/tasksApi.js";
import { listLocations, type LocationRow } from "../lib/locationsApi.js";
import { listRoutes, listRouteLocations, type RouteRow } from "../lib/routesApi.js";
import { listOfficers, type OfficerRow } from "../lib/usersApi.js";
import { useDashboardSession } from "../auth/useDashboardSession.js";
import { useConfirmDialog } from "../components/ConfirmDialog.js";
import { formatTaipeiDate } from "../../lib/time.js";

const ROLE_LABEL: Record<string, string> = {
  tenant_admin: "管理員", manager: "主管", site_supervisor: "案場督導",
  control_center_operator: "中控操作員", officer: "巡邏員",
  incident_reporter: "通報員", auditor: "稽核員", client_viewer: "客戶檢視",
};

type ListState =
  | { status: "loading" }
  | { status: "ready"; rows: AssignmentRow[]; task: TaskRow }
  | { status: "error"; message: string };

type EditState = null | { mode: "new" } | { mode: "edit"; row: AssignmentRow };

export default function TaskAssignments(): React.ReactElement {
  const { taskId } = useParams<{ taskId: string }>();
  const session = useDashboardSession();
  const tenantParam = session.super_admin ? session.acting_tenant_id ?? null : null;
  const canWrite = session.scopes.includes("write:site") || session.super_admin === true;
  const [state, setState] = useState<ListState>({ status: "loading" });
  const [editing, setEditing] = useState<EditState>(null);
  const [locations, setLocations] = useState<LocationRow[]>([]);
  const [routes, setRoutes] = useState<RouteRow[]>([]);
  const [officers, setOfficers] = useState<OfficerRow[]>([]);
  /** Set after a save whose response carried `route_extended: true`. The
   *  banner sits above the list for a few seconds so the operator sees
   *  the side-effect ("路線被擴充了") without being interrupted by a
   *  modal. Click 關閉 or trigger another save to dismiss. */
  const [routeExtendedToast, setRouteExtendedToast] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!taskId) return;
    setState({ status: "loading" });
    try {
      const [task, list, cps, rts, ofs] = await Promise.all([
        getTask(taskId, tenantParam),
        listAssignmentsForTask(taskId, tenantParam),
        listLocations({ tenantId: tenantParam, status: "active", limit: 500 }).then((r) => r.items),
        listRoutes({ tenantId: tenantParam, status: "active" }).then((r) => r.items),
        listOfficers({ tenantId: tenantParam, status: "active" }).then((r) => r.items),
      ]);
      setLocations(cps);
      setRoutes(rts);
      setOfficers(ofs);
      setState({ status: "ready", rows: list.items, task });
    } catch (err) {
      setState({ status: "error", message: friendlyAssignmentsError(err) });
    }
  }, [taskId, tenantParam]);

  useEffect(() => { void load(); }, [load]);

  const cpLookup = useMemo(() => Object.fromEntries(locations.map((c) => [c.id, c])), [locations]);
  const rtLookup = useMemo(() => Object.fromEntries(routes.map((r) => [r.id, r])), [routes]);
  const ofLookup = useMemo(() => Object.fromEntries(officers.map((o) => [o.id, o])), [officers]);

  if (!taskId) return <div className="text-sm text-danger">缺少 task id</div>;

  return (
    <>
      <PageHeader
        title={state.status === "ready" ? `${state.task.name} — 派發` : "任務派發"}
        description="任務在哪些 (地點 × 路線 × 時間段 × 對象) 出現"
        actions={
          <>
            <Link to="/dashboard/tasks" className="btn-secondary text-sm">
              <ArrowLeft className="h-4 w-4" /> 回任務列表
            </Link>
            <button className="btn-secondary text-sm" onClick={() => void load()} disabled={state.status === "loading"}>
              <RefreshCw className={`h-4 w-4 ${state.status === "loading" ? "animate-spin" : ""}`} /> 重新整理
            </button>
            {canWrite && (
              <button data-tour-id="task-assignments.new" className="btn-primary text-sm" onClick={() => setEditing({ mode: "new" })} disabled={locations.length === 0}>
                <Plus className="h-4 w-4" /> 新增派發
              </button>
            )}
          </>
        }
      />
      {routeExtendedToast && (
        <div className="mb-3 flex items-center gap-2 rounded-lg border border-success/40 bg-success/10 p-3 text-sm text-success">
          <Info className="h-4 w-4" />
          <span className="flex-1 text-text-primary">{routeExtendedToast}</span>
          <button className="text-2xs underline text-text-muted" onClick={() => setRouteExtendedToast(null)}>關閉</button>
        </div>
      )}
      {state.status === "loading" && <ListSkeleton />}
      {state.status === "error" && (
        <EmptyState icon={ServerCrash} title="派發列表載入失敗" description={state.message}
          action={<button className="btn-primary text-sm" onClick={() => void load()}>再試一次</button>} />
      )}
      {state.status === "ready" && state.rows.length === 0 && (
        <EmptyState icon={Inbox} title="此任務尚未派發到任何地點"
          description="新增至少一筆派發後，巡邏員感應對應地點時才會看到這個任務。"
          action={canWrite && locations.length > 0
            ? <button data-tour-id="task-assignments.new" className="btn-primary text-sm" onClick={() => setEditing({ mode: "new" })}>新增派發</button>
            : undefined} />
      )}
      {state.status === "ready" && state.rows.length > 0 && (
        <div className="space-y-2">
          {state.rows.map((a) => (
            <AssignmentCard
              key={a.id}
              row={a}
              location={cpLookup[a.location_id]}
              route={a.route_id ? rtLookup[a.route_id] : null}
              officer={a.target_kind === "officer" ? ofLookup[a.target_value] : null}
              onClick={canWrite ? () => setEditing({ mode: "edit", row: a }) : undefined}
            />
          ))}
        </div>
      )}

      <Drawer
        open={!!editing}
        onClose={() => setEditing(null)}
        title={editing?.mode === "edit" ? "編輯派發" : "新增派發"}
        description="地點 × 路線 × RRULE × 對象"
        size="lg"
      >
        {editing && (
          <AssignmentForm
            mode={editing.mode}
            taskId={taskId}
            row={editing.mode === "edit" ? editing.row : undefined}
            tenantId={tenantParam}
            locations={locations}
            routes={routes}
            officers={officers}
            onClose={() => setEditing(null)}
            onSaved={(info) => {
              setEditing(null);
              if (info.routeExtended) {
                setRouteExtendedToast(`已將地點加入「${info.routeName ?? "此路線"}」路線。`);
              }
              void load();
            }}
            canArchive={canWrite}
          />
        )}
      </Drawer>
    </>
  );
}

function AssignmentCard({ row, location, route, officer, onClick }: {
  row: AssignmentRow;
  location?: LocationRow;
  route?: RouteRow | null;
  officer?: OfficerRow | null;
  onClick?: () => void;
}): React.ReactElement {
  const validFromLocal = formatTaipeiDate(row.valid_from * 1000);
  const validToLocal = row.valid_to ? formatTaipeiDate(row.valid_to * 1000) : "永久";
  return (
    <button
      onClick={onClick}
      className="w-full text-left card hover:bg-bg-hover transition-colors p-4 space-y-2"
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1 text-sm font-medium text-text-primary">
          <MapPin className="h-3.5 w-3.5 text-accent" />
          {location?.label ?? row.location_id.slice(0, 8) + "…"}
        </span>
        {route ? (
          <span className="inline-flex items-center gap-1 text-2xs text-text-secondary">
            <RouteIcon className="h-3 w-3" /> {route.name}
          </span>
        ) : (
          <span className="text-2xs text-text-muted">（任意路線）</span>
        )}
        {row.status === "archived" && (
          <span className="badge-default text-2xs">已封存</span>
        )}
      </div>
      <p className="text-xs text-text-secondary">{humanizeRRule(row.rrule)}</p>
      <div className="flex flex-wrap items-center gap-3 text-2xs text-text-muted">
        <span className="inline-flex items-center gap-1">
          <Clock className="h-3 w-3" />
          {humanizeWindow(row.duration_minutes, row.grace_before_min, row.grace_after_min)}
        </span>
        <span className="inline-flex items-center gap-1">
          {row.target_kind === "officer" ? <UserIcon className="h-3 w-3" /> : <UsersIcon className="h-3 w-3" />}
          {row.target_kind === "officer"
            ? (officer?.display_name ?? row.target_value.slice(0, 8) + "…")
            : (ROLE_LABEL[row.target_value] ?? row.target_value)}
        </span>
        <span>有效 {validFromLocal} – {validToLocal}</span>
      </div>
    </button>
  );
}

function AssignmentForm({ mode, taskId, row, tenantId, locations, routes, officers, onClose, onSaved, canArchive }: {
  mode: "new" | "edit";
  taskId: string;
  row?: AssignmentRow;
  tenantId: string | null;
  locations: LocationRow[];
  routes: RouteRow[];
  officers: OfficerRow[];
  onClose: () => void;
  /** Called after a successful save. `routeExtended` is true when the
   *  server auto-added the selected location to the selected route's
   *  route_locations — caller shows a toast confirming the side effect. */
  onSaved: (info: { routeExtended: boolean; routeName: string | null }) => void;
  canArchive: boolean;
}): React.ReactElement {
  const [locationId, setLocationId] = useState(row?.location_id ?? locations[0]?.id ?? "");
  const [routeId, setRouteId] = useState(row?.route_id ?? "");
  /** Location IDs in the selected route's `route_locations`. null while
   *  loading or when no route is picked (== "全部 location" mode). When
   *  the user picks a route the Location dropdown filters to this set
   *  so they can't accidentally bind the task to a location that isn't
   *  actually on the route — the wizard matches on (route, cp) exactly
   *  and silently drops mismatches. */
  const [routeCpIds, setRouteCpIds] = useState<Set<string> | null>(null);
  const [routeCpLoading, setRouteCpLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!routeId) { setRouteCpIds(null); return; }
    setRouteCpLoading(true);
    void (async () => {
      try {
        const r = await listRouteLocations(routeId, tenantId);
        if (!cancelled) setRouteCpIds(new Set(r.items.map((it) => it.location_id)));
      } catch {
        if (!cancelled) setRouteCpIds(new Set());
      } finally {
        if (!cancelled) setRouteCpLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [routeId, tenantId]);

  /** Filtered Location dropdown options. When no route is picked, show
   *  every active location in the tenant. When a route is picked, show
   *  only the locations that exist in its `route_locations`. */
  const filteredLocations = useMemo(() => {
    if (!routeId || !routeCpIds) return locations;
    return locations.filter((c) => routeCpIds.has(c.id));
  }, [locations, routeId, routeCpIds]);

  const locationMismatch = !!routeId && !!routeCpIds && !!locationId && !routeCpIds.has(locationId);
  const [rrule, setRrule] = useState(row?.rrule ?? "FREQ=DAILY;BYHOUR=9;BYMINUTE=0");
  // Numeric fields are stored as strings so the user can backspace down
  // to empty without React re-stamping a "0" back into the box (the
  // bug: Number("") was being coerced into 0 → controlled value={0}
  // forced the input back to "0" on every render). Parse on save.
  const [duration, setDuration] = useState(String(row?.duration_minutes ?? 60));
  const [graceBefore, setGraceBefore] = useState(String(row?.grace_before_min ?? 5));
  const [graceAfter, setGraceAfter] = useState(String(row?.grace_after_min ?? 30));
  const [validFrom, setValidFrom] = useState<string>(
    toDateInput(row?.valid_from ? new Date(row.valid_from * 1000) : new Date()));
  const [validTo, setValidTo] = useState<string>(
    row?.valid_to ? toDateInput(new Date(row.valid_to * 1000)) : "");
  const [targetKind, setTargetKind] = useState<TargetKind>(row?.target_kind ?? "role");
  const [targetValue, setTargetValue] = useState<string>(row?.target_value ?? "officer");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    setBusy(true); setErr(null);
    const body: AssignmentBody = {
      location_id: locationId,
      route_id: routeId || null,
      rrule,
      duration_minutes: parseIntOr(duration, 60),
      grace_before_min: parseIntOr(graceBefore, 0),
      grace_after_min: parseIntOr(graceAfter, 0),
      valid_from: Math.floor(new Date(validFrom).getTime() / 1000),
      valid_to: validTo ? Math.floor(new Date(validTo).getTime() / 1000 + 86400) : null,
      target_kind: targetKind,
      target_value: targetValue,
    };
    try {
      const result = mode === "new"
        ? await createAssignment(taskId, body, tenantId)
        : row ? await patchAssignment(row.id, body, tenantId)
              : null;
      onSaved({ routeExtended: !!result?.route_extended, routeName: routes.find((r) => r.id === routeId)?.name ?? null });
    } catch (e) { setErr(friendlyAssignmentsError(e)); }
    finally { setBusy(false); }
  };

  const dlg = useConfirmDialog();
  const archive = async () => {
    if (!row) return;
    if (!(await dlg.confirm("確定要封存此派發？任務在原 (地點 × 路線 × 時間 × 對象) 將不再生效。", { destructive: true, okLabel: "封存" }))) return;
    setBusy(true); setErr(null);
    try { await archiveAssignment(row.id, tenantId); onSaved({ routeExtended: false, routeName: null }); }
    catch (e) { setErr(friendlyAssignmentsError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      {/* 路線先選 — 地點下拉會跟著過濾，避免指派到不在路線上的點。 */}
      <Field label="路線（選填）" hint="先選路線，下方地點會自動過濾為路線上的點。留空＝這個任務在地點上「任意路線 / 無路線」時都會觸發。">
        <select className="select" value={routeId} onChange={(e) => setRouteId(e.target.value)}>
          <option value="">— 任意路線 / 無路線 —</option>
          {routes.map((r) => (
            <option key={r.id} value={r.id}>{r.name}</option>
          ))}
        </select>
      </Field>

      <Field
        label="地點"
        required
        hint={routeId
          ? (routeCpLoading
              ? "正在讀取此路線的地點…"
              : `僅顯示此路線上的 ${filteredLocations.length} 個地點`)
          : "顯示此單位全部地點"}
      >
        <select
          className={`select ${locationMismatch ? "border-warning" : ""}`}
          value={locationId}
          onChange={(e) => setLocationId(e.target.value)}
        >
          {filteredLocations.length === 0 && !routeCpLoading && (
            <option value="">
              {routeId ? "— 此路線尚未加入任何地點 —" : "— 尚無地點 —"}
            </option>
          )}
          {filteredLocations.map((c) => (
            <option key={c.id} value={c.id}>{c.label} · {c.site_name ?? c.site_id}</option>
          ))}
        </select>
        {locationMismatch && (
          <p className="mt-1.5 inline-flex items-start gap-1 text-2xs text-text-muted leading-relaxed">
            <Info className="h-3 w-3 mt-0.5 flex-shrink-0" />
            <span>儲存時會自動把這個地點加入「{routes.find((r) => r.id === routeId)?.name ?? "此路線"}」路線。</span>
          </p>
        )}
      </Field>

      <Field label="時間規則 (RRULE)" required>
        <RRuleEditor
          value={rrule}
          onChange={setRrule}
          validFrom={Math.floor(new Date(validFrom).getTime() / 1000)}
        />
      </Field>

      <div className="grid grid-cols-3 gap-3">
        <Field label="持續時間（分鐘）">
          <input type="number" min={1} max={1440} className="input font-mono"
                 value={duration} onChange={(e) => setDuration(e.target.value)} />
        </Field>
        <Field label="向前寬限（分）">
          <input type="number" min={0} max={1440} className="input font-mono"
                 value={graceBefore} onChange={(e) => setGraceBefore(e.target.value)} />
        </Field>
        <Field label="向後寬限（分）">
          <input type="number" min={0} max={1440} className="input font-mono"
                 value={graceAfter} onChange={(e) => setGraceAfter(e.target.value)} />
        </Field>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Field label="有效起始日">
          <input type="date" className="input" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} />
        </Field>
        <Field label="有效結束日（留空 = 永久）">
          <input type="date" className="input" value={validTo} onChange={(e) => setValidTo(e.target.value)} />
        </Field>
      </div>

      <fieldset className="rounded-md border border-border bg-bg-raised p-4 space-y-3">
        <legend className="text-sm font-semibold px-2">對象</legend>
        <div className="flex gap-2">
          <button type="button" onClick={() => { setTargetKind("role"); setTargetValue("officer"); }}
            className={`flex-1 h-10 rounded-md text-sm ${targetKind === "role" ? "bg-accent text-text-inverse" : "bg-bg-base text-text-secondary"}`}>
            <UsersIcon className="h-4 w-4 inline mr-1" /> 角色
          </button>
          <button type="button" onClick={() => { setTargetKind("officer"); setTargetValue(officers[0]?.id ?? ""); }}
            className={`flex-1 h-10 rounded-md text-sm ${targetKind === "officer" ? "bg-accent text-text-inverse" : "bg-bg-base text-text-secondary"}`}>
            <UserIcon className="h-4 w-4 inline mr-1" /> 個別巡邏員
          </button>
        </div>
        {targetKind === "role" ? (
          <select className="select" value={targetValue} onChange={(e) => setTargetValue(e.target.value)}>
            {Object.entries(ROLE_LABEL).map(([k, v]) => (
              <option key={k} value={k}>{v} · {k}</option>
            ))}
          </select>
        ) : (
          <select className="select" value={targetValue} onChange={(e) => setTargetValue(e.target.value)}>
            {officers.length === 0 && <option value="">— 尚無 active 巡邏員 —</option>}
            {officers.map((o) => (
              <option key={o.id} value={o.id}>{o.display_name}</option>
            ))}
          </select>
        )}
      </fieldset>

      {err && <div className="rounded border border-danger/40 bg-danger/10 p-3 text-sm text-danger">{err}</div>}

      <div className="flex flex-row items-center justify-between gap-2">
        <div>
          {mode === "edit" && row && canArchive && (
            <button className="btn-danger text-sm" onClick={() => void archive()} disabled={busy}>
              <Trash2 className="h-4 w-4" /> 封存
            </button>
          )}
        </div>
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="btn-secondary text-sm" disabled={busy}>取消</button>
          <button data-tour-id="task-assignments.save" onClick={() => void save()} className="btn-primary text-sm"
            disabled={busy || !locationId || !rrule.trim() || !targetValue}>
            {busy ? "儲存中…" : "儲存"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ListSkeleton(): React.ReactElement {
  return (
    <div className="space-y-2">
      {[0, 1, 2].map((i) => (
        <div key={i} className="card p-4 space-y-2">
          <Skeleton className="h-5 w-2/3" />
          <Skeleton className="h-4 w-1/2" />
          <Skeleton className="h-4 w-3/4" />
        </div>
      ))}
    </div>
  );
}

function toDateInput(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** Parse the controlled-string value of a `<input type="number">` back to
 *  an integer for the API call. Empty / NaN falls back to `fallback` so
 *  the user can backspace down to nothing without breaking the request. */
function parseIntOr(s: string, fallback: number): number {
  const n = parseInt(s, 10);
  return Number.isFinite(n) ? n : fallback;
}
